import { isSocialPostUrl } from '@dupe/common/socialUrl'
import { isValidUrl } from './helpers'

/**
 * Determines if a prompt string is likely a URL
 *
 * This is more strict than isValidUrl() alone because:
 * - isValidUrl() prepends https:// to any string, so "blue jeans" becomes "https://blue jeans"
 * - We need to check if the prompt LOOKS like a URL before validating it
 * - This prevents false positives for search queries that happen to contain dots
 *
 * Examples:
 * - "blue jeans" → false (search query with spaces)
 * - "test.py" → false (looks like file, but validated by isValidUrl)
 * - "config.json" → false (looks like file, but validated by isValidUrl)
 * - "nike.com" → true (domain with TLD)
 * - "example.xyz" → true (domain with any TLD)
 * - "https://wayfair.com" → true (full URL)
 * - "www.amazon.com" → true (www prefix)
 * - "hello.company is great" → false (contains spaces)
 *
 * @param prompt - The prompt string to check
 * @returns true if the prompt is likely a URL, false otherwise
 */
export function isPromptUrl(prompt: string | null | undefined): boolean {
  if (!prompt) return false

  // Trim whitespace for checking
  const trimmed = prompt.trim()

  // If it contains spaces, it's likely a search query, not a URL
  // Exception: URLs can have spaces in query params, but those would start with http/https
  if (trimmed.includes(' ') && !trimmed.startsWith('http')) {
    return false
  }

  // Common file extensions to exclude (these are not URLs)
  const fileExtensions =
    /\.(py|js|ts|tsx|jsx|json|css|xml|txt|md|pdf|doc|docx|xls|xlsx|zip|tar|gz|jpg|jpeg|png|gif|svg|mp4|mp3|avi)$/i
  if (fileExtensions.test(trimmed)) {
    return false
  }

  // Bare .html filenames (no scheme, no www, no path separators) are file
  // references, not URLs — but full URLs ending in .html are legitimate
  // product pages (e.g. ashleyfurniture.com/p/foo.html).
  const isBareHtmlFilename =
    /\.html?$/i.test(trimmed) &&
    !trimmed.includes('://') &&
    !trimmed.startsWith('www.') &&
    !trimmed.includes('/')
  if (isBareHtmlFilename) return false

  // Strict URL check: must clearly look like a URL
  const looksLikeUrl =
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('www.') ||
    // Domain pattern: subdomain(s).domain.tld
    // This catches example.xyz, test.tk, amazon.co.uk, ebay.com.au, etc.
    /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}(\/.*)?$/.test(trimmed)

  // Double-check with isValidUrl to ensure it's actually a valid URL
  const isUrl = looksLikeUrl && !!isValidUrl(trimmed)
  return isUrl
}

/**
 * A pasted URL the brain should search as a product page (`forceUrlSearch`).
 * Not a social post link (YouTube, TikTok, Instagram, Reddit): those are not
 * product pages, and the fragrance chat reads them with `readSocialPost`
 * instead. `isPromptUrl` itself still calls them URLs, so paste-link
 * analytics and pathway labels are unchanged.
 */
export function isProductUrlPrompt(prompt: string | null | undefined): boolean {
  if (!isPromptUrl(prompt)) return false
  return !isSocialPostUrl((prompt ?? '').trim())
}
